/**
 * The settings tabs, and the mapping between a tab and its address.
 *
 * The tabs are routed rather than local state so a bookmark, a browser back
 * and a link from elsewhere in the app all land on the same section — and so
 * the shell's `shellNavKey` keeps marking "Einstellungen" as the current
 * destination for every one of them (it matches the `/einstellungen` prefix).
 *
 * "Abrechnung" is drawn as a tab on `Einstellungen.dc.html`, linking to
 * `BillingSettings`. It is deliberately not here: that screen is not built and
 * a tab that navigates to a 404 is worse than one that is missing.
 *
 * "Verwaltung" carries what the separate admin portal used to: the jobs of
 * every form and the state of the system. "API-Zugang" carries the API keys,
 * and is deliberately **not** admin-only — a key belongs to an account, the
 * gateway's key endpoints are open to every signed-in caller and scoped to
 * them, so gating them behind a role would lock people out of their own keys.
 */
export type SettingsTab = 'profil' | 'auswertung' | 'apiZugang' | 'verwaltung'

export interface SettingsTabSpec {
  value: SettingsTab
  label: string
  /** The path segment under `/einstellungen`; empty for the default tab. */
  segment: string
  /** Rendered only for an administrator — and refused on a direct URL too. */
  adminOnly?: boolean
}

export const SETTINGS_TABS: SettingsTabSpec[] = [
  { value: 'profil', label: 'Profil & Sicherheit', segment: '' },
  { value: 'auswertung', label: 'Auswertung', segment: 'auswertung' },
  { value: 'apiZugang', label: 'API-Zugang', segment: 'api-zugang' },
  { value: 'verwaltung', label: 'Verwaltung', segment: 'verwaltung', adminOnly: true },
]

export const SETTINGS_BASE_PATH = '/einstellungen'

/** The artboards set 220px on the account-level tab strips. */
export const SETTINGS_TAB_WIDTH = 220

/**
 * Which tab a URL segment names. `undefined` (bare `/einstellungen`) is the
 * first tab; anything unknown is `null`, so the page can redirect rather
 * than silently showing the profile under a wrong address.
 */
export function settingsTabFromSegment(segment?: string): SettingsTab | null {
  if (!segment) return SETTINGS_TABS[0].value
  const hit = SETTINGS_TABS.find((tab) => tab.segment === segment)
  return hit ? hit.value : null
}

export function settingsTabPath(tab: SettingsTab): string {
  const spec = SETTINGS_TABS.find((entry) => entry.value === tab)
  return spec && spec.segment ? `${SETTINGS_BASE_PATH}/${spec.segment}` : SETTINGS_BASE_PATH
}

export function settingsTabLabel(tab: SettingsTab): string {
  return SETTINGS_TABS.find((entry) => entry.value === tab)?.label ?? ''
}

/**
 * The tabs this person may see. An admin-only tab is absent for everyone else
 * — not disabled and not greyed: a control nobody may use is noise, and a
 * greyed "Verwaltung" would advertise a screen rather than hide it.
 */
export function visibleSettingsTabs(isAdmin: boolean): SettingsTabSpec[] {
  return SETTINGS_TABS.filter((tab) => !tab.adminOnly || isAdmin)
}

/** True when the tab must be refused to a non-administrator, tab strip or not. */
export function settingsTabRequiresAdmin(tab: SettingsTab): boolean {
  return SETTINGS_TABS.find((entry) => entry.value === tab)?.adminOnly === true
}
